import {useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {ArrowLeft, Loader2, Zap, Users, ChevronRight} from "lucide-react";
import * as m from "@/paraglide/messages";
import {usePost, useSuggestions, useLaunchBots} from "@/hooks/use-influence";
import {Button} from "@/components/ui/button";
import {Textarea} from "@/components/ui/textarea";
import {Slider} from "@/components/ui/slider";
import {ScrollArea} from "@/components/ui/scroll-area";
import {SocialPostCard} from "@/components/widgets/social-post-card";

export function PostPage() {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const [botCount, setBotCount] = useState(10);
  const [actionDescription, setActionDescription] = useState("");

  const {data: post, isLoading: postLoading} = usePost(id);
  const {data: suggestionsData, isLoading: suggestionsLoading} = useSuggestions(id);
  const launchMutation = useLaunchBots({
    onSuccess: () => navigate(-1),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !actionDescription.trim()) return;
    launchMutation.mutate({
      post_id: id,
      bot_count: botCount,
      action_description: actionDescription,
    });
  };

  const handleSuggestionClick = (text: string) => {
    setActionDescription(text);
  };

  if (postLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-zinc-500">{m.post_page_not_found()}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          {m.post_page_go_back()}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 p-6 pb-0">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          {m.post_page_back()}
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="min-h-0 flex-1">
        <div className="mx-auto max-w-2xl space-y-6 p-6">
          <SocialPostCard post={post} hideInfluenceButton />

          <div className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950">
            <div
              className="pointer-events-none absolute inset-0 opacity-30"
              style={{
                backgroundImage: `radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
                                 radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)`,
              }}
            />
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />

            <div className="relative p-6">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-zinc-700/50 to-zinc-800/30 ring-1 ring-zinc-600/40">
                    <Zap className="h-5 w-5 text-zinc-300" />
                  </div>
                  <div>
                    <h2 className="font-mono text-sm font-medium tracking-wide text-zinc-100 uppercase">
                      {m.post_campaign_title()}
                    </h2>
                    <p className="text-xs text-zinc-500">Configure narrative deployment</p>
                  </div>
                </div>
                <div className="flex h-2 w-2 animate-pulse rounded-full bg-zinc-400 shadow-[0_0_8px_rgba(255,255,255,0.4)]" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-zinc-500" />
                      <span className="text-sm text-zinc-400">
                        {m.post_campaign_bot_count_label()}
                      </span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="font-mono text-2xl font-bold text-zinc-100">{botCount}</span>
                      <span className="text-xs text-zinc-500">agents</span>
                    </div>
                  </div>
                  <div className="relative">
                    <Slider
                      value={[botCount]}
                      onValueChange={(v: number[]) => setBotCount(v[0])}
                      min={1}
                      max={100}
                      step={1}
                      className="[&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:border-2 [&_[role=slider]]:border-zinc-400 [&_[role=slider]]:bg-zinc-900 [&_[role=slider]]:shadow-[0_0_10px_rgba(255,255,255,0.2)]"
                    />
                    <div className="mt-2 flex justify-between font-mono text-[10px] text-zinc-600 uppercase">
                      <span>1</span>
                      <span>25</span>
                      <span>50</span>
                      <span>75</span>
                      <span>100</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-sm text-zinc-400">{m.post_campaign_action_label()}</label>
                  <Textarea
                    placeholder={m.post_campaign_action_placeholder()}
                    value={actionDescription}
                    onChange={(e) => setActionDescription(e.target.value)}
                    rows={4}
                    className="resize-none border-zinc-800 bg-zinc-900/50 text-zinc-200 placeholder:text-zinc-600 focus:border-zinc-600 focus:ring-zinc-700/30"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium tracking-wider text-zinc-500 uppercase">
                      {m.post_campaign_suggestions_label()}
                    </span>
                    {suggestionsLoading && (
                      <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                        <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-zinc-400" />
                        <span>{m.post_campaign_suggestions_loading()}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!suggestionsLoading &&
                      suggestionsData?.suggestions?.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => handleSuggestionClick(s.text)}
                          className="group relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/80 px-3 py-2 text-left text-sm text-zinc-300 transition-all hover:border-zinc-600 hover:bg-zinc-800/80"
                        >
                          <span className="relative z-10">{s.text}</span>
                          <ChevronRight className="absolute top-1/2 right-2 h-3 w-3 -translate-y-1/2 text-zinc-600 opacity-0 transition-all group-hover:opacity-100" />
                        </button>
                      ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!actionDescription.trim() || launchMutation.isPending}
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-zinc-200 to-white p-px font-medium text-zinc-900 shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.25)] disabled:opacity-50 disabled:shadow-none"
                >
                  <div className="relative flex items-center justify-center gap-2 rounded-[11px] bg-gradient-to-r from-zinc-200 to-white px-6 py-3 transition-all group-hover:from-white group-hover:to-zinc-100">
                    {launchMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4" />
                    )}
                    <span className="font-mono text-sm tracking-wide uppercase">
                      {m.post_campaign_submit({count: botCount})}
                    </span>
                  </div>
                </button>
              </form>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
